// ============================================================
//  src/pages/rent-space/RentSpacePage.js
//
//  User-facing "Rent Space" — register a WhatsApp number under the
//  platform's own WABA and pay monthly via PocketFi, without needing
//  a Meta developer account. Same 4-step Meta registration flow as
//  PhoneRegistrationWizard, but the platform supplies the WhatsApp
//  token/WABA ID (never shown to the user), plus a payment step at
//  the end instead of a "copy your keys" success screen.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import './RentSpacePage.css';
import {
  getRentSpaceInfo, getMyRentals,
  rentSpaceAddNumber, rentSpaceRequestCode, rentSpaceVerifyCode, rentSpaceComplete,
  rentSpaceRetryPayment, rentSpaceRenew, createBotFromRental,
} from '../../api/client';

// Below this many seconds remaining, a still-active rental starts showing
// an urgent "renew soon" warning instead of a plain countdown. 3 days.
const RENEWAL_WARNING_SECONDS = 3 * 24 * 60 * 60;

/** "expired" | "3d 4h" | "12h 5m" | null (no countdown applies) */
function formatCountdown(secondsRemaining) {
  if (secondsRemaining == null) return null;
  if (secondsRemaining <= 0) return 'expired';
  const days  = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);
  const mins  = Math.floor((secondsRemaining % 3600) / 60);
  if (days > 0)  return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const COUNTRY_CODES = [
  ['234', 'Nigeria (+234)'], ['1', 'US / Canada (+1)'], ['44', 'UK (+44)'],
  ['91', 'India (+91)'], ['27', 'South Africa (+27)'], ['254', 'Kenya (+254)'],
  ['233', 'Ghana (+233)'], ['256', 'Uganda (+256)'], ['255', 'Tanzania (+255)'],
];

const STATUS_META = {
  pending_payment: { label: 'Pending Payment', color: '#9a7b00', bg: '#fff8e1' },
  active:          { label: 'Active',          color: '#00875a', bg: '#e7f9f0' },
  paused:          { label: 'Paused',           color: '#6b7280', bg: '#f3f4f6' },
  deregistered:    { label: 'Deregistered',     color: '#c0392b', bg: '#fdeaea' },
};

function extractErrorMessage(err, fallback) {
  return err?.response?.data?.message ?? fallback;
}

export default function RentSpacePage() {
  const [loading, setLoading]   = useState(true);
  const [info, setInfo]         = useState(null);     // { monthlyPrice, available, maxPerUser, canRentMore }
  const [rentals, setRentals]   = useState([]);        // every rental this customer holds
  // Only shown once the customer already has at least one rental and asks
  // for another — a brand-new customer with zero rentals goes straight to
  // the wizard instead (see render below), same as before.
  const [showAddAnother, setShowAddAnother] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [infoRes, rentalsRes] = await Promise.all([getRentSpaceInfo(), getMyRentals()]);
      if (infoRes.data?.success) setInfo(infoRes.data);
      if (rentalsRes.data?.success) {
        setRentals(rentalsRes.data.rentals ?? []);
        // Re-loading after a fresh rental finished registering closes the
        // "add another" panel automatically — no reason to keep the form
        // open once it's done its job.
        setShowAddAnother(false);
      }
    } catch {
      toast.error('Could not load Rent Space');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Every branch below renders into this single shell — nothing else in
  // this file introduces its own `.rsp-page`/`.rsp-shell` wrapper. That
  // used to be the case (RentalStatusView wrapped itself in `.rsp-page`
  // too), which put multiple flex-row siblings directly under a
  // `display:flex` page container and scattered the cards sideways
  // instead of stacking them — the layout bug behind the "not looking
  // good" screenshot. Keeping exactly one wrapper here is what fixes it.
  let body;

  if (loading) {
    body = <div className="rsp-loading">Loading your spaces…</div>;
  } else if (!info?.available) {
    body = (
      <div className="rsp-card rsp-empty">
        <div className="rsp-empty-icon">🏠</div>
        <div className="rsp-empty-title">Rent Space isn't available yet</div>
        <div className="rsp-empty-text">The platform hasn't finished setting this up. Check back soon.</div>
      </div>
    );
  } else {
    // A rental that's mid-registration (started but never finished the
    // Meta verification steps) always resumes into the wizard, regardless
    // of how many other complete rentals the customer already has.
    const unfinished = rentals.find(r => r.rnStatus === 'pending_payment' && !r.rnPhoneNumberId);

    if (rentals.length === 0 && !showAddAnother) {
      // No rentals at all yet → the simple first-time wizard.
      body = <RentWizardView monthlyPrice={info.monthlyPrice} onRentalStarted={load} />;
    } else if (unfinished) {
      body = <RentWizardView monthlyPrice={info.monthlyPrice} resumeRnId={unfinished.rnId} onRentalStarted={load} />;
    } else if (showAddAnother) {
      body = (
        <RentWizardView
          monthlyPrice={info.monthlyPrice}
          onRentalStarted={load}
          onCancel={() => setShowAddAnother(false)}
        />
      );
    } else {
      body = (
        <>
          <div className="rsp-spaces-header">
            <div>
              <div className="rsp-spaces-title">Your Rented Spaces</div>
              <div className="rsp-spaces-sub">
                {info.spacesUsed} of {info.maxPerUser > 0 ? info.maxPerUser : 'unlimited'} space{info.spacesUsed === 1 ? '' : 's'} in use
              </div>
            </div>
            <button className="rsp-link-btn" onClick={load}>↻ Refresh</button>
          </div>

          <div className="rsp-stack">
            {rentals.map(r => (
              <RentalStatusView key={r.rnId} rental={r} onRefresh={load} />
            ))}
          </div>

          {info.canRentMore ? (
            <button className="rsp-add-space-card" onClick={() => setShowAddAnother(true)}>
              <span className="rsp-add-space-icon">+</span>
              <span>
                <span className="rsp-add-space-title">Rent another number</span>
                <span className="rsp-add-space-sub">₦{Number(info.monthlyPrice).toLocaleString()}/month · run a second bot on a different number</span>
              </span>
            </button>
          ) : (
            <div className="rsp-banner warning">
              You've reached your limit of {info.maxPerUser} rented space{info.maxPerUser === 1 ? '' : 's'}.
              Contact support if you need more.
            </div>
          )}
        </>
      );
    }
  }

  return (
    <div className="rsp-page">
      <div className="rsp-shell">{body}</div>
    </div>
  );
}

const STATUS_EXPLANATIONS = {
  deregistered: 'This number has been deregistered and can no longer send or receive messages through this platform. Contact support if you believe this is a mistake.',
};

// ── Status view — already renting (or mid-payment) ──────────────
function RentalStatusView({ rental, onRefresh }) {
  const meta = STATUS_META[rental.rnStatus] ?? STATUS_META.pending_payment;
  const isPendingUnregistered = rental.rnStatus === 'pending_payment' && !rental.rnPhoneNumberId;

  const [retrying, setRetrying] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [showCreateBot, setShowCreateBot] = useState(false);
  const [botName, setBotName] = useState('');
  const [creating, setCreating] = useState(false);

  if (isPendingUnregistered) {
    // Registration was started but never finished (e.g. they left mid-flow)
    return <RentWizardView monthlyPrice={rental.rnMonthlyAmount} resumeRnId={rental.rnId} onRentalStarted={onRefresh} />;
  }

  const countdown = rental.rnStatus === 'active' ? formatCountdown(rental.secondsRemaining) : null;
  const isUrgent  = rental.isExpired || (rental.secondsRemaining != null && rental.secondsRemaining <= RENEWAL_WARNING_SECONDS);

  async function handleRenew() {
    setRenewing(true);
    try {
      const res = await rentSpaceRenew(rental.rnId);
      if (res.data?.success) {
        window.location.href = res.data.paymentLink;
      } else {
        toast.error(res.data?.message ?? 'Could not start payment');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Network error'));
    }
    setRenewing(false);
  }

  async function handleRetryPayment() {
    setRetrying(true);
    try {
      const res = await rentSpaceRetryPayment(rental.rnId);
      if (res.data?.success) {
        window.location.href = res.data.paymentLink;
      } else {
        toast.error(res.data?.message ?? 'Could not start payment');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setRetrying(false);
  }

  async function handleCreateBot(e) {
    e.preventDefault();
    if (!botName.trim()) return toast.error('Bot name is required');
    setCreating(true);
    try {
      const res = await createBotFromRental(rental.rnId, botName.trim());
      if (res.data?.success) {
        toast.success('Bot created! Find it on your Dashboard.');
        onRefresh();
      } else {
        toast.error(res.data?.message ?? 'Could not create bot');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setCreating(false);
  }

  return (
    <div className="rsp-card">
      <div className="rsp-card-hdr">
        <span>{rental.rnDisplayName || 'Rented Number'}</span>
        <button className="rsp-link-btn" onClick={onRefresh}>↻ Refresh</button>
      </div>
      <div className="rsp-status-row">
        <span className="rsp-status-badge" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
      </div>
      <div className="rsp-detail-row"><span>Number</span><strong>+{rental.rnCountryCode}{rental.rnPhoneNumber}</strong></div>
      <div className="rsp-detail-row"><span>Display name</span><strong>{rental.rnDisplayName}</strong></div>
      <div className="rsp-detail-row"><span>Phone number ID</span><strong className="mono">{rental.rnPhoneNumberId || '—'}</strong></div>
      {rental.rnStatus === 'deregistered' && (
        <div className="rsp-banner warning">⚠ {STATUS_EXPLANATIONS.deregistered}</div>
      )}
      <div className="rsp-detail-row"><span>Monthly fee</span><strong>₦{Number(rental.rnMonthlyAmount).toLocaleString()}</strong></div>
      {rental.rnNextBillingAt && (
        <div className="rsp-detail-row"><span>Next billing</span><strong>{new Date(rental.rnNextBillingAt).toLocaleDateString()}</strong></div>
      )}

      {countdown && (
        <div className={`rsp-countdown ${isUrgent ? 'urgent' : ''}`}>
          <span className="rsp-countdown-icon">{rental.isExpired ? '⏸' : '⏳'}</span>
          <span>
            {rental.isExpired
              ? 'Expired — renew now to reactivate your bot'
              : <>Renews in <strong>{countdown}</strong>{isUrgent ? ' — renew soon' : ''}</>}
          </span>
        </div>
      )}
      {countdown && (rental.isExpired || isUrgent) && (
        <button className="rsp-btn primary full" onClick={handleRenew} disabled={renewing} style={{ marginTop: 10 }}>
          {renewing ? 'Starting payment…' : `Renew now — ₦${Number(rental.rnMonthlyAmount).toLocaleString()}`}
        </button>
      )}
      {rental.isExpired && rental.rnLinkedBotId && (
        <div className="rsp-banner warning">
          This space's payment is overdue, so its bot has stopped responding to messages until it's renewed.
        </div>
      )}

      {rental.rnStatus === 'active' && rental.rnPhoneNumberId && !rental.rnLinkedBotId && !showCreateBot && (
        <>
          <div className="rsp-banner info">
            This number is ready to use. Create a bot with it below — no token or setup needed, we handle that
            automatically.
          </div>
          <button className="rsp-btn primary full" onClick={() => setShowCreateBot(true)}>
            Create a bot with this number
          </button>
        </>
      )}
      {rental.rnStatus === 'active' && rental.rnLinkedBotId && (
        <div className="rsp-banner info">A bot is already using this number. Find it on your Dashboard.</div>
      )}
      {showCreateBot && (
        <form onSubmit={handleCreateBot} style={{ marginTop: 10 }}>
          <div className="rsp-field">
            <label className="rsp-label">Bot name</label>
            <input className="rsp-input" placeholder="e.g. Notifier Bot" value={botName}
              onChange={e => setBotName(e.target.value)} autoFocus />
          </div>
          <button type="submit" className="rsp-btn primary full" disabled={creating}>
            {creating ? 'Creating…' : 'Create bot'}
          </button>
        </form>
      )}

      {rental.rnStatus === 'paused' && (
        <div className="rsp-banner warning">
          This number is currently paused. Contact support if you believe this is a mistake.
        </div>
      )}
      {rental.rnStatus === 'pending_payment' && rental.rnPhoneNumberId && (
        <>
          <div className="rsp-banner warning">
            Registration finished, but payment wasn't completed. Complete payment below to activate this number
            — contact support if you were charged but this still shows pending.
          </div>
          <button className="rsp-btn primary full" onClick={handleRetryPayment} disabled={retrying}>
            {retrying ? 'Starting payment…' : `Complete payment — ₦${Number(rental.rnMonthlyAmount).toLocaleString()}`}
          </button>
        </>
      )}
    </div>
  );
}

// ── Wizard view — start (or resume) renting a number ─────────────
function RentWizardView({ monthlyPrice, resumeRnId, onRentalStarted, onCancel }) {
  const [step, setStep] = useState(resumeRnId ? 'code-choice' : 'intro'); // intro | details | code-choice | code-entry | pin
  const [busy, setBusy] = useState(false);
  const [rnId, setRnId] = useState(resumeRnId || null);
  const [apiError, setApiError] = useState(null);

  const [form, setForm] = useState({ cc: '234', phoneNumber: '', displayName: '' });
  const [codeMethod, setCodeMethod] = useState('SMS');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (apiError) setApiError(null); };

  async function handleAddNumber(e) {
    e.preventDefault();
    setApiError(null);
    if (!form.phoneNumber.trim())  return toast.error('Phone number is required');
    if (!form.displayName.trim())  return toast.error('Display name is required');

    setBusy(true);
    try {
      const res = await rentSpaceAddNumber(form.cc, form.phoneNumber.trim(), form.displayName.trim());
      if (res.data?.success) {
        setRnId(res.data.rnId);
        setStep('code-choice');
      } else {
        setApiError(res.data?.message ?? 'Could not add this number');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleSendCode(method) {
    setCodeMethod(method);
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceRequestCode(rnId, method);
      if (res.data?.success) {
        toast.success(`Code sent via ${method}`);
        setStep('code-entry');
      } else {
        setApiError(res.data?.message ?? 'Could not send the code');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  function handleDigitChange(i, val) {
    if (!/^\d?$/.test(val)) return;
    const next = [...codeDigits];
    next[i] = val;
    setCodeDigits(next);
    if (val && i < 5) document.getElementById(`rsp-code-digit-${i + 1}`)?.focus();
    if (val && i === 5 && next.every(d => d !== '')) handleVerifyCode(next.join(''));
  }

  async function handleVerifyCode(codeOverride) {
    const code = codeOverride ?? codeDigits.join('');
    if (code.length !== 6) return toast.error('Enter all 6 digits');
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceVerifyCode(rnId, code);
      if (res.data?.success) {
        toast.success('Number verified');
        setStep('pin');
      } else {
        setApiError(res.data?.message ?? 'That code didn\'t work');
        setCodeDigits(['', '', '', '', '', '']);
        document.getElementById('rsp-code-digit-0')?.focus();
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleComplete(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) return toast.error('PIN must be exactly 6 digits');
    if (pin !== pinConfirm)   return toast.error('PINs do not match');
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceComplete(rnId, pin, pinConfirm);
      if (res.data?.success) {
        toast.success('Registered! Redirecting you to payment…');
        onRentalStarted();
        window.location.href = res.data.paymentLink;
      } else {
        setApiError(res.data?.message ?? 'Registration failed');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  return (
    <div className="rsp-card">
      {step === 'intro' && (
        <>
          <div className="rsp-card-hdr"><span>Rent a WhatsApp number</span></div>
          <p className="rsp-intro-text">
            Register your own WhatsApp number under this platform's WhatsApp Business Account — no Meta developer
            account, no App Setup screens, no technical steps on your end. You'll go through the same phone
            verification WhatsApp always requires (a code sent by text or call), then pay a simple monthly fee.
          </p>
          <div className="rsp-price-box">
            <div className="rsp-price-amount">₦{Number(monthlyPrice).toLocaleString()}</div>
            <div className="rsp-price-label">per month</div>
          </div>
          <button className="rsp-btn primary full" onClick={() => setStep('details')}>Get started</button>
          {onCancel && (
            <button className="rsp-link-btn" style={{ marginTop: 12 }} onClick={onCancel}>← Back to my spaces</button>
          )}
        </>
      )}

      {step !== 'intro' && (
        <>
          <div className="rsp-card-hdr"><span>Rent a WhatsApp number</span></div>
          {apiError && (
            <div className="rsp-banner warning">
              <div>{apiError}</div>
              <button type="button" className="rsp-link-btn" onClick={() => setApiError(null)} style={{ marginTop: 6 }}>Dismiss</button>
            </div>
          )}

          {step === 'details' && (
            <form onSubmit={handleAddNumber}>
              <div className="rsp-row-2">
                <div className="rsp-field">
                  <label className="rsp-label">Country</label>
                  <select className="rsp-select" value={form.cc} onChange={e => set('cc', e.target.value)}>
                    {COUNTRY_CODES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                  </select>
                </div>
                <div className="rsp-field">
                  <label className="rsp-label">Phone number</label>
                  <input className="rsp-input" placeholder="8012345678" value={form.phoneNumber}
                    onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, ''))} />
                  <div className="rsp-hint">Without the country code or leading 0</div>
                </div>
              </div>
              <div className="rsp-field">
                <label className="rsp-label">Display name</label>
                <input className="rsp-input" placeholder="What customers will see" value={form.displayName}
                  onChange={e => set('displayName', e.target.value)} />
              </div>
              <button type="submit" className="rsp-btn primary full" disabled={busy}>
                {busy ? 'Adding…' : 'Add number'}
              </button>
              {onCancel && (
                <button type="button" className="rsp-link-btn" style={{ marginTop: 12 }} onClick={onCancel}>← Back to my spaces</button>
              )}
            </form>
          )}

          {step === 'code-choice' && (
            <>
              <p className="rsp-intro-text">
                How should we send you the verification code for <strong>+{form.cc}{form.phoneNumber}</strong>?
              </p>
              <div className="rsp-choice-row">
                <button type="button" className="rsp-choice-card" disabled={busy} onClick={() => handleSendCode('SMS')}>
                  📱<br />Text me a code
                </button>
                <button type="button" className="rsp-choice-card" disabled={busy} onClick={() => handleSendCode('VOICE')}>
                  📞<br />Call me with a code
                </button>
              </div>
            </>
          )}

          {step === 'code-entry' && (
            <>
              <p className="rsp-intro-text">Enter the 6-digit code we sent you.</p>
              <div className="rsp-otp-row">
                {codeDigits.map((d, i) => (
                  <input key={i} id={`rsp-code-digit-${i}`} className="rsp-otp-digit" inputMode="numeric" maxLength={1}
                    value={d} disabled={busy} onChange={e => handleDigitChange(i, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Backspace' && !d && i > 0) document.getElementById(`rsp-code-digit-${i - 1}`)?.focus(); }} />
                ))}
              </div>
              <div className="rsp-otp-actions">
                <button type="button" className="rsp-link-btn" disabled={busy} onClick={() => handleSendCode(codeMethod)}>Resend code</button>
                <button type="button" className="rsp-btn primary" disabled={busy} onClick={() => handleVerifyCode()}>
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </>
          )}

          {step === 'pin' && (
            <form onSubmit={handleComplete}>
              <div className="rsp-banner info">
                Set a 6-digit PIN for this number's security. You won't need to remember this yourself — we
                store it securely for you.
              </div>
              <div className="rsp-row-2">
                <div className="rsp-field">
                  <label className="rsp-label">6-digit PIN</label>
                  <input className="rsp-input" inputMode="numeric" maxLength={6} placeholder="••••••"
                    value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} />
                </div>
                <div className="rsp-field">
                  <label className="rsp-label">Confirm PIN</label>
                  <input className="rsp-input" inputMode="numeric" maxLength={6} placeholder="••••••"
                    value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, ''))} />
                </div>
              </div>
              <button type="submit" className="rsp-btn primary full" disabled={busy}>
                {busy ? 'Completing…' : `Complete & pay ₦${Number(monthlyPrice).toLocaleString()}`}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}